import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Tab="CURRENT"|"TRANSITION"|"TRANSFER"|"HISTORY";

type AcademicYear={
  academicYearId:string;
  yearCode:string;
  startsOn:string;
  endsOn:string;
  status:string;
};

type School={
  schoolId:string;
  name:string;
  code?:string|null;
};

type Enrollment={
  enrollmentId:string;
  academicYearId?:string;
  yearCode:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  status:string;
  enrolledOn?:string|null;
  endedOn?:string|null;
};

type LifecycleEvent={
  eventId:string;
  eventType:string;
  eventDate:string;
  fromGradeLevel?:string|null;
  toGradeLevel?:string|null;
  fromClassLabel?:string|null;
  toClassLabel?:string|null;
  relatedSchoolName?:string|null;
  reason?:string|null;
};

type Student={
  learnerId:string;
  displayName:string;
  credentialCount:number;
  journeyCount:number;
  currentEnrollment?:Enrollment|null;
  enrollmentHistory:Enrollment[];
  recentEvents:LifecycleEvent[];
};

type Transfer={
  transferId:string;
  learnerId:string;
  displayName:string;
  transferKind:string;
  status:string;
  transferDate:string;
  sourceSchoolId:string;
  sourceSchoolName:string;
  destinationSchoolId?:string|null;
  destinationSchoolName?:string|null;
  reason?:string|null;
  version:number;
};

type TransitionBatch={
  batchId:string;
  status:string;
  sourceAcademicYearId:string;
  sourceYearCode:string;
  targetAcademicYearId:string;
  targetYearCode:string;
  targetStartsOn:string;
  itemCount:number;
  summary:Record<string,unknown>;
  createdAt:string;
  appliedAt?:string|null;
};

type Workspace={
  canManage:boolean;
  counts:{
    activeStudents:number;
    plannedStudents:number;
    outgoingTransfers:number;
    incomingTransfers:number;
    preparedTransitions:number;
  };
  academicYears:AcademicYear[];
  schools:School[];
  students:Student[];
  transfers:Transfer[];
  transitionBatches:TransitionBatch[];
};

type TransitionRow={
  learnerId:string;
  displayName:string;
  fromGrade:string;
  fromClass:string;
  action:"PROMOTE"|"REPEAT"|"GRADUATE";
  toGrade:string;
  toClass:string;
  selected:boolean;
};

function enrollmentStatus(value:string){
  const labels:Record<string,string>={
    ACTIVE:"재학",
    PLANNED:"입학 예정",
    TRANSFERRED:"전출",
    COMPLETED:"수료·졸업",
    WITHDRAWN:"중도이동"
  };
  return labels[value] || value;
}

function eventLabel(value:string){
  const labels:Record<string,string>={
    ENROLLED:"입학·등록",
    PLACEMENT_CHANGED:"학년·반 변경",
    PROMOTION_PREPARED:"학년이동 준비",
    PROMOTED:"진급",
    REPEATED:"유급·재학년",
    TRANSFER_REQUESTED:"전학 요청",
    TRANSFER_OUT:"전출",
    TRANSFER_IN:"전입",
    EXTERNAL_TRANSFER_IN:"외부학교 전입",
    WITHDRAWN:"중도이동",
    GRADUATED:"졸업",
    REENROLLED:"재입학"
  };
  return labels[value] || value;
}

function nextGrade(value?:string|null){
  const grade=Number(value);
  if(!Number.isFinite(grade))return "";
  return grade>=12 ? "" : String(grade+1);
}

function todayIso(){
  return new Date().toISOString().slice(0,10);
}

export function CenterStudentLifecyclePage({
  client,
  schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const [data,setData]=useState<Workspace|null>(null);
  const [tab,setTab]=useState<Tab>("CURRENT");
  const [query,setQuery]=useState("");
  const [selectedLearnerId,setSelectedLearnerId]=useState("");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  const [editGrade,setEditGrade]=useState("");
  const [editClass,setEditClass]=useState("");
  const [editReason,setEditReason]=useState("");

  const [yearCode,setYearCode]=useState("");
  const [yearStart,setYearStart]=useState("");
  const [yearEnd,setYearEnd]=useState("");
  const [targetYearId,setTargetYearId]=useState("");
  const [transitionRows,setTransitionRows]=useState<TransitionRow[]>([]);

  const [transferLearnerId,setTransferLearnerId]=useState("");
  const [transferDate,setTransferDate]=useState(todayIso());
  const [transferMode,setTransferMode]=useState<"INTERNAL"|"EXTERNAL">("EXTERNAL");
  const [destinationSchoolId,setDestinationSchoolId]=useState("");
  const [destinationSchoolName,setDestinationSchoolName]=useState("");
  const [transferReason,setTransferReason]=useState("");

  const [acceptTransferId,setAcceptTransferId]=useState("");
  const [acceptYearId,setAcceptYearId]=useState("");
  const [acceptGrade,setAcceptGrade]=useState("");
  const [acceptClass,setAcceptClass]=useState("");
  const [acceptStudentKey,setAcceptStudentKey]=useState("");

  const [inName,setInName]=useState("");
  const [inSourceSchool,setInSourceSchool]=useState("");
  const [inYearId,setInYearId]=useState("");
  const [inGrade,setInGrade]=useState("");
  const [inClass,setInClass]=useState("");
  const [inDate,setInDate]=useState(todayIso());
  const [inStudentKey,setInStudentKey]=useState("");
  const [inEmail,setInEmail]=useState("");

  const [withdrawDate,setWithdrawDate]=useState(todayIso());
  const [withdrawReason,setWithdrawReason]=useState("");

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_student_lifecycle_workspace",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      setData(next);
      setSelectedLearnerId((current)=>
        next.students.some((item)=>item.learnerId===current)
          ? current
          : next.students.find((item)=>item.currentEnrollment?.status==="ACTIVE")?.learnerId
            || next.students[0]?.learnerId
            || ""
      );
      setTransferLearnerId((current)=>
        next.students.some((item)=>item.learnerId===current && item.currentEnrollment?.status==="ACTIVE")
          ? current
          : next.students.find((item)=>item.currentEnrollment?.status==="ACTIVE")?.learnerId || ""
      );
      const activeOrPlanned=next.academicYears.find((item)=>item.status==="ACTIVE") || next.academicYears.find((item)=>item.status==="PLANNED");
      setInYearId((current)=>next.academicYears.some((item)=>item.academicYearId===current) ? current : activeOrPlanned?.academicYearId || "");
      setAcceptYearId((current)=>next.academicYears.some((item)=>item.academicYearId===current) ? current : activeOrPlanned?.academicYearId || "");
      const planned=next.academicYears.find((item)=>item.status==="PLANNED");
      setTargetYearId((current)=>next.academicYears.some((item)=>item.academicYearId===current && item.status==="PLANNED") ? current : planned?.academicYearId || "");
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const selectedStudent=useMemo(
    ()=>data?.students.find((item)=>item.learnerId===selectedLearnerId) || null,
    [data,selectedLearnerId]
  );

  useEffect(()=>{
    setEditGrade(selectedStudent?.currentEnrollment?.gradeLevel || "");
    setEditClass(selectedStudent?.currentEnrollment?.classLabel || "");
    setEditReason("");
  },[selectedLearnerId,selectedStudent?.currentEnrollment?.enrollmentId]);

  const activeStudents=useMemo(
    ()=>(data?.students || []).filter((item)=>item.currentEnrollment?.status==="ACTIVE"),
    [data]
  );

  const filteredStudents=useMemo(()=>{
    const q=query.trim().toLowerCase();
    return (data?.students || []).filter((item)=>{
      if(!q)return true;
      const e=item.currentEnrollment;
      return [item.displayName,e?.yearCode,e?.gradeLevel,e?.classLabel]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  },[data,query]);

  useEffect(()=>{
    if(!data || !targetYearId){
      setTransitionRows([]);
      return;
    }
    setTransitionRows(activeStudents.map((student)=>{
      const grade=student.currentEnrollment?.gradeLevel || "";
      const graduating=Number(grade)>=12;
      return {
        learnerId:student.learnerId,
        displayName:student.displayName,
        fromGrade:grade,
        fromClass:student.currentEnrollment?.classLabel || "",
        action:graduating ? "GRADUATE" : "PROMOTE",
        toGrade:graduating ? "" : nextGrade(grade),
        toClass:student.currentEnrollment?.classLabel || "",
        selected:true
      };
    }));
  },[data?.students,targetYearId]);

  async function run(key:string,fn:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success?:string){
    setBusy(key);
    setError("");
    setMessage("");
    try{
      const result=await fn();
      if(result.error)throw new Error(result.error.message);
      if(success)setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{
      setBusy("");
    }
  }

  async function savePlacement(){
    if(!selectedStudent)return;
    await run("placement",()=>client.rpc("center_change_student_placement",{
      p_school_id:schoolId,
      p_learner_id:selectedStudent.learnerId,
      p_grade_level:editGrade,
      p_class_label:editClass || null,
      p_reason:editReason || null
    }),"학년·반 정보를 변경했습니다.");
  }

  async function createYear(){
    const id=await run("year",()=>client.rpc("center_create_academic_year",{
      p_school_id:schoolId,
      p_year_code:yearCode.trim(),
      p_starts_on:yearStart,
      p_ends_on:yearEnd
    }),"다음 학년도를 만들었습니다.");
    if(id){
      setYearCode("");setYearStart("");setYearEnd("");
    }
  }

  async function prepareTransition(){
    const rows=transitionRows.filter((row)=>row.selected).map((row)=>({
      learnerId:row.learnerId,
      action:row.action,
      toGradeLevel:row.action==="GRADUATE" ? null : row.toGrade,
      toClassLabel:row.action==="GRADUATE" ? null : row.toClass
    }));
    if(!targetYearId || !rows.length)return;
    await run("transition",()=>client.rpc("center_prepare_academic_transition",{
      p_school_id:schoolId,
      p_target_academic_year_id:targetYearId,
      p_rows:rows
    }),`${rows.length}명의 학년이동안을 저장했습니다.`);
  }

  async function requestTransfer(){
    if(!transferLearnerId)return;
    await run("transfer",()=>client.rpc("center_request_student_transfer",{
      p_school_id:schoolId,
      p_learner_id:transferLearnerId,
      p_transfer_date:transferDate,
      p_destination_school_id:transferMode==="INTERNAL" ? destinationSchoolId || null : null,
      p_destination_school_name:transferMode==="EXTERNAL" ? destinationSchoolName.trim() || null : null,
      p_reason:transferReason.trim() || null
    }),"전학 요청을 등록했습니다.");
    setDestinationSchoolId("");setDestinationSchoolName("");setTransferReason("");
  }

  async function registerExternalTransferIn(){
    await run("transfer-in",()=>client.rpc("center_register_external_transfer_in",{
      p_school_id:schoolId,
      p_academic_year_id:inYearId,
      p_display_name:inName.trim(),
      p_grade_level:inGrade,
      p_class_label:inClass.trim() || null,
      p_source_school_name:inSourceSchool.trim(),
      p_transfer_date:inDate,
      p_student_key:inStudentKey.trim() || null,
      p_email:inEmail.trim() || null
    }),"외부학교 전입생을 등록했습니다.");
    setInName("");setInSourceSchool("");setInGrade("");setInClass("");setInStudentKey("");setInEmail("");
  }

  if(!data)return <p className="muted">학생 학적 원장을 불러오는 중…</p>;

  const preparedBatch=data.transitionBatches.find((item)=>item.status==="PREPARED" && item.targetAcademicYearId===targetYearId);
  const incoming=data.transfers.filter((item)=>item.destinationSchoolId===schoolId && item.status==="REQUESTED");
  const outgoing=data.transfers.filter((item)=>item.sourceSchoolId===schoolId && item.status==="REQUESTED");

  return (
    <section className="student-erp-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Student Master ERP</p>
          <h2>학적관리</h2>
          <p className="route-description">
            학년·반 이동, 학년도 전환, 전입·전출을 관리해도 학생의 진로기록과 디지털배지는 같은 학생에게 계속 이어집니다.
          </p>
        </div>
        <div className="erp-continuity-mark">
          <span>Life Passport</span>
          <strong>학생 ID 연속</strong>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="student-erp-metrics">
        <Metric label="재학생" value={data.counts.activeStudents} />
        <Metric label="입학 예정" value={data.counts.plannedStudents} />
        <Metric label="전출 대기" value={data.counts.outgoingTransfers} />
        <Metric label="전입 대기" value={data.counts.incomingTransfers} />
        <Metric label="학년이동 준비" value={data.counts.preparedTransitions} />
      </div>

      <nav className="student-erp-tabs">
        {([
          ["CURRENT","재학생"],
          ["TRANSITION","학년이동"],
          ["TRANSFER","전입·전출"],
          ["HISTORY","학적이력"]
        ] as Array<[Tab,string]>).map(([value,label])=>(
          <button key={value} className={tab===value ? "active" : ""} onClick={()=>setTab(value)}>{label}</button>
        ))}
      </nav>

      {tab==="CURRENT" && (
        <div className="student-erp-layout">
          <aside className="student-master-list">
            <input className="teacher-search" value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="학생 이름·학년·반 검색" />
            <div className="student-master-scroll">
              {filteredStudents.map((student)=>(
                <button
                  key={student.learnerId}
                  className={selectedLearnerId===student.learnerId ? "active" : ""}
                  onClick={()=>setSelectedLearnerId(student.learnerId)}
                >
                  <span><strong>{student.displayName}</strong><small>
                    {student.currentEnrollment
                      ? `${student.currentEnrollment.gradeLevel || "-"}학년 · ${student.currentEnrollment.classLabel || "-"}반`
                      : "현재 재학정보 없음"}
                  </small></span>
                  <b>{student.credentialCount} 배지</b>
                </button>
              ))}
            </div>
          </aside>

          <div className="student-master-detail">
            {selectedStudent ? (
              <>
                <header className="student-master-head">
                  <div>
                    <p className="eyebrow">학생 Master</p>
                    <h3>{selectedStudent.displayName}</h3>
                    <p>
                      {selectedStudent.currentEnrollment
                        ? `${selectedStudent.currentEnrollment.yearCode} · ${selectedStudent.currentEnrollment.gradeLevel || "-"}학년 · ${selectedStudent.currentEnrollment.classLabel || "-"}반`
                        : "현재 재학정보 없음"}
                    </p>
                  </div>
                  <div className="student-master-badge">
                    <span>누적 디지털배지</span>
                    <strong>{selectedStudent.credentialCount}</strong>
                  </div>
                </header>

                <div className="student-master-stats">
                  <Metric label="학적 이력" value={selectedStudent.enrollmentHistory.length} />
                  <Metric label="진로 여정" value={selectedStudent.journeyCount} />
                  <Metric label="디지털배지" value={selectedStudent.credentialCount} />
                </div>

                {data.canManage && selectedStudent.currentEnrollment?.status==="ACTIVE" && (
                  <section className="student-erp-card">
                    <div className="panel-title"><strong>학년·반 변경</strong><span>변경 전·후 이력이 자동 저장됩니다.</span></div>
                    <div className="student-placement-form">
                      <label>학년<input type="number" min="1" max="12" value={editGrade} onChange={(e)=>setEditGrade(e.target.value)} /></label>
                      <label>반<input value={editClass} onChange={(e)=>setEditClass(e.target.value)} /></label>
                      <label className="wide">변경 사유<input value={editReason} onChange={(e)=>setEditReason(e.target.value)} placeholder="예: 반배정 변경" /></label>
                      <button className="primary" disabled={busy!=="" || !editGrade} onClick={savePlacement}>학적 변경 저장</button>
                    </div>
                  </section>
                )}

                {data.canManage && selectedStudent.currentEnrollment?.status==="ACTIVE" && (
                  <details className="student-danger-details">
                    <summary>중도이동·재학종료 처리</summary>
                    <div className="student-danger-form">
                      <label>처리일<input type="date" value={withdrawDate} onChange={(e)=>setWithdrawDate(e.target.value)} /></label>
                      <label>사유<input value={withdrawReason} onChange={(e)=>setWithdrawReason(e.target.value)} placeholder="사유 입력" /></label>
                      <button
                        className="secondary danger"
                        disabled={busy!=="" || !withdrawReason.trim()}
                        onClick={()=>run("withdraw",()=>client.rpc("center_withdraw_student",{
                          p_school_id:schoolId,
                          p_learner_id:selectedStudent.learnerId,
                          p_effective_date:withdrawDate,
                          p_reason:withdrawReason
                        }),"재학 종료를 기록했습니다. 디지털배지와 Life Passport는 유지됩니다.")}
                      >
                        재학 종료
                      </button>
                    </div>
                  </details>
                )}
              </>
            ) : <p className="case-empty">학생을 선택하세요.</p>}
          </div>
        </div>
      )}

      {tab==="TRANSITION" && (
        <div className="student-transition-stack">
          {data.canManage && (
            <details className="teacher-setup-details">
              <summary><span><strong>다음 학년도 만들기</strong><small>학년이동안을 만들기 전에 1회 설정</small></span><b>설정</b></summary>
              <div className="academic-year-form">
                <label>학년도<input value={yearCode} onChange={(e)=>setYearCode(e.target.value)} placeholder="2027" /></label>
                <label>시작일<input type="date" value={yearStart} onChange={(e)=>setYearStart(e.target.value)} /></label>
                <label>종료일<input type="date" value={yearEnd} onChange={(e)=>setYearEnd(e.target.value)} /></label>
                <button className="primary" disabled={busy!=="" || !yearCode || !yearStart || !yearEnd} onClick={createYear}>학년도 만들기</button>
              </div>
            </details>
          )}

          <section className="student-erp-card">
            <div className="transition-head">
              <div><p className="eyebrow">학년도 전환</p><h3>진급·재학년·졸업을 한 번에 준비합니다</h3></div>
              <select value={targetYearId} onChange={(e)=>setTargetYearId(e.target.value)}>
                <option value="">다음 학년도 선택</option>
                {data.academicYears.filter((item)=>item.status==="PLANNED").map((year)=>(
                  <option key={year.academicYearId} value={year.academicYearId}>{year.yearCode} · {year.startsOn}</option>
                ))}
              </select>
            </div>

            {targetYearId ? (
              <>
                <div className="transition-table">
                  <div className="transition-row head"><span>선택</span><span>학생</span><span>현재</span><span>처리</span><span>다음 학년</span><span>다음 반</span></div>
                  {transitionRows.map((row,index)=>(
                    <div className="transition-row" key={row.learnerId}>
                      <input type="checkbox" checked={row.selected} onChange={(e)=>setTransitionRows((current)=>current.map((item,i)=>i===index?{...item,selected:e.target.checked}:item))} />
                      <strong>{row.displayName}</strong>
                      <span>{row.fromGrade || "-"}학년 · {row.fromClass || "-"}반</span>
                      <select value={row.action} onChange={(e)=>setTransitionRows((current)=>current.map((item,i)=>i===index?{...item,action:e.target.value as TransitionRow["action"],toGrade:e.target.value==="GRADUATE"?"":item.toGrade}:item))}>
                        <option value="PROMOTE">진급</option>
                        <option value="REPEAT">재학년</option>
                        <option value="GRADUATE">졸업</option>
                      </select>
                      {row.action==="GRADUATE" ? <span>-</span> : <input type="number" min="1" max="12" value={row.toGrade} onChange={(e)=>setTransitionRows((current)=>current.map((item,i)=>i===index?{...item,toGrade:e.target.value}:item))} />}
                      {row.action==="GRADUATE" ? <span>-</span> : <input value={row.toClass} onChange={(e)=>setTransitionRows((current)=>current.map((item,i)=>i===index?{...item,toClass:e.target.value}:item))} />}
                    </div>
                  ))}
                </div>

                <div className="transition-actions">
                  {preparedBatch ? (
                    <>
                      <div><strong>{preparedBatch.targetYearCode} 학년이동안 준비됨</strong><span>{preparedBatch.itemCount}명 · 적용일 {preparedBatch.targetStartsOn}</span></div>
                      <button className="secondary" disabled={busy!==""} onClick={()=>run("transition-cancel",()=>client.rpc("center_cancel_academic_transition",{p_school_id:schoolId,p_batch_id:preparedBatch.batchId}),"학년이동안을 취소했습니다.")}>준비안 취소</button>
                      {data.canManage && (
                        <button className="primary" disabled={busy!=="" || todayIso()<preparedBatch.targetStartsOn} onClick={()=>run("year-start",()=>client.rpc("center_start_academic_year",{p_school_id:schoolId,p_target_academic_year_id:preparedBatch.targetAcademicYearId}),"새 학년도를 시작하고 학년이동을 적용했습니다.")}>
                          {todayIso()<preparedBatch.targetStartsOn ? `${preparedBatch.targetStartsOn}부터 적용 가능` : "새 학년도 시작·적용"}
                        </button>
                      )}
                    </>
                  ) : (
                    <button className="primary" disabled={busy!=="" || !transitionRows.some((row)=>row.selected)} onClick={prepareTransition}>선택 학생 학년이동안 저장</button>
                  )}
                </div>
              </>
            ) : <p className="case-empty">먼저 다음 학년도를 만들거나 선택하세요.</p>}
          </section>
        </div>
      )}

      {tab==="TRANSFER" && (
        <div className="transfer-erp-grid">
          <section className="student-erp-card">
            <div className="panel-title"><strong>전출 요청</strong><span>내부학교 전학은 같은 학생 ID로 기록이 이어집니다.</span></div>
            <div className="transfer-form">
              <label>학생<select value={transferLearnerId} onChange={(e)=>setTransferLearnerId(e.target.value)}>
                {activeStudents.map((student)=><option key={student.learnerId} value={student.learnerId}>{student.displayName} · {student.currentEnrollment?.gradeLevel || "-"}학년 {student.currentEnrollment?.classLabel || "-"}반</option>)}
              </select></label>
              <label>전학일<input type="date" value={transferDate} onChange={(e)=>setTransferDate(e.target.value)} /></label>
              <label>구분<select value={transferMode} onChange={(e)=>setTransferMode(e.target.value as "INTERNAL"|"EXTERNAL")}>
                <option value="EXTERNAL">외부학교 전출</option>
                <option value="INTERNAL">RS CAREER 사용학교 간 전학</option>
              </select></label>
              {transferMode==="INTERNAL" ? (
                <label>전입 학교<select value={destinationSchoolId} onChange={(e)=>setDestinationSchoolId(e.target.value)}>
                  <option value="">학교 선택</option>
                  {data.schools.map((school)=><option key={school.schoolId} value={school.schoolId}>{school.name}</option>)}
                </select></label>
              ) : (
                <label>전입 학교명<input value={destinationSchoolName} onChange={(e)=>setDestinationSchoolName(e.target.value)} /></label>
              )}
              <label className="wide">사유<input value={transferReason} onChange={(e)=>setTransferReason(e.target.value)} /></label>
              <button className="primary" disabled={busy!=="" || !transferLearnerId || (transferMode==="INTERNAL" ? !destinationSchoolId : !destinationSchoolName.trim())} onClick={requestTransfer}>전학 요청 등록</button>
            </div>
          </section>

          {data.canManage && (
            <section className="student-erp-card">
              <div className="panel-title"><strong>외부학교 전입 등록</strong><span>새 학생 Master를 만들고 진로여정을 시작합니다.</span></div>
              <div className="transfer-form">
                <label>학생 이름<input value={inName} onChange={(e)=>setInName(e.target.value)} /></label>
                <label>이전 학교<input value={inSourceSchool} onChange={(e)=>setInSourceSchool(e.target.value)} /></label>
                <label>학년도<select value={inYearId} onChange={(e)=>setInYearId(e.target.value)}>
                  {data.academicYears.filter((year)=>["ACTIVE","PLANNED"].includes(year.status)).map((year)=><option key={year.academicYearId} value={year.academicYearId}>{year.yearCode}</option>)}
                </select></label>
                <label>전입일<input type="date" value={inDate} onChange={(e)=>setInDate(e.target.value)} /></label>
                <label>학년<input type="number" min="1" max="12" value={inGrade} onChange={(e)=>setInGrade(e.target.value)} /></label>
                <label>반<input value={inClass} onChange={(e)=>setInClass(e.target.value)} /></label>
                <label>학생코드<input value={inStudentKey} onChange={(e)=>setInStudentKey(e.target.value)} /></label>
                <label>배지 수령 이메일<input type="email" value={inEmail} onChange={(e)=>setInEmail(e.target.value)} /></label>
                <button className="primary" disabled={busy!=="" || !inName.trim() || !inSourceSchool.trim() || !inYearId || !inGrade} onClick={registerExternalTransferIn}>전입생 등록</button>
              </div>
            </section>
          )}

          <section className="student-erp-card transfer-list-card">
            <div className="panel-title"><strong>전입·전출 진행</strong><span>대기 {incoming.length+outgoing.length}건</span></div>
            <div className="transfer-case-list">
              {[...incoming,...outgoing].map((item)=>(
                <article key={item.transferId}>
                  <div>
                    <strong>{item.displayName}</strong>
                    <span>{item.sourceSchoolName} → {item.destinationSchoolName || "외부학교"}</span>
                    <small>{item.transferDate} · {item.transferKind==="INTERNAL" ? "내부 전학" : "외부 전출"}</small>
                  </div>
                  <div className="case-actions">
                    {item.destinationSchoolId===schoolId && item.status==="REQUESTED" && data.canManage && (
                      <button onClick={()=>{setAcceptTransferId(item.transferId);setAcceptGrade("");setAcceptClass("");}}>전입 승인</button>
                    )}
                    {item.sourceSchoolId===schoolId && item.transferKind==="EXTERNAL_OUT" && data.canManage && (
                      <button className="primary" disabled={busy!==""} onClick={()=>run("transfer-complete",()=>client.rpc("center_complete_external_transfer",{p_school_id:schoolId,p_transfer_id:item.transferId}),"전출을 확정했습니다. 학생의 배지와 진로기록은 유지됩니다.")}>전출 확정</button>
                    )}
                    {item.sourceSchoolId===schoolId && data.canManage && (
                      <button className="secondary" disabled={busy!==""} onClick={()=>run("transfer-cancel",()=>client.rpc("center_cancel_student_transfer",{p_school_id:schoolId,p_transfer_id:item.transferId}),"전학 요청을 취소했습니다.")}>취소</button>
                    )}
                  </div>
                </article>
              ))}
              {!incoming.length && !outgoing.length && <p className="case-empty">진행 중인 전입·전출이 없습니다.</p>}
            </div>

            {acceptTransferId && data.canManage && (
              <div className="transfer-accept-box">
                <strong>전입 승인 정보</strong>
                <select value={acceptYearId} onChange={(e)=>setAcceptYearId(e.target.value)}>
                  {data.academicYears.filter((year)=>["ACTIVE","PLANNED"].includes(year.status)).map((year)=><option key={year.academicYearId} value={year.academicYearId}>{year.yearCode}</option>)}
                </select>
                <input type="number" min="1" max="12" value={acceptGrade} onChange={(e)=>setAcceptGrade(e.target.value)} placeholder="학년" />
                <input value={acceptClass} onChange={(e)=>setAcceptClass(e.target.value)} placeholder="반" />
                <input value={acceptStudentKey} onChange={(e)=>setAcceptStudentKey(e.target.value)} placeholder="새 학교 학생코드(선택)" />
                <button className="primary" disabled={busy!=="" || !acceptYearId || !acceptGrade} onClick={async()=>{
                  const result=await run("transfer-accept",()=>client.rpc("center_accept_internal_transfer",{
                    p_school_id:schoolId,
                    p_transfer_id:acceptTransferId,
                    p_academic_year_id:acceptYearId,
                    p_grade_level:acceptGrade,
                    p_class_label:acceptClass || null,
                    p_student_key:acceptStudentKey || null
                  }),"전입을 승인했습니다. 기존 진로기록과 디지털배지가 그대로 이어집니다.");
                  if(result)setAcceptTransferId("");
                }}>전입 확정</button>
              </div>
            )}
          </section>
        </div>
      )}

      {tab==="HISTORY" && (
        <div className="student-history-layout">
          <aside className="student-master-list">
            <input className="teacher-search" value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="학생 검색" />
            <div className="student-master-scroll">
              {filteredStudents.map((student)=>(
                <button key={student.learnerId} className={selectedLearnerId===student.learnerId ? "active" : ""} onClick={()=>setSelectedLearnerId(student.learnerId)}>
                  <span><strong>{student.displayName}</strong><small>{student.enrollmentHistory.length}개 학적 · {student.credentialCount}개 배지</small></span>
                </button>
              ))}
            </div>
          </aside>
          <div className="student-history-detail">
            {selectedStudent ? (
              <>
                <header><div><p className="eyebrow">누적 학생 기록</p><h3>{selectedStudent.displayName}</h3></div><div className="student-master-badge"><span>디지털배지</span><strong>{selectedStudent.credentialCount}</strong></div></header>
                <section className="history-section">
                  <h4>학적 이력</h4>
                  {selectedStudent.enrollmentHistory.map((item)=>(
                    <div className="history-line" key={item.enrollmentId}>
                      <span><strong>{item.yearCode}</strong><small>{item.gradeLevel || "-"}학년 · {item.classLabel || "-"}반</small></span>
                      <b>{enrollmentStatus(item.status)}</b>
                    </div>
                  ))}
                </section>
                <section className="history-section">
                  <h4>이동·변경 이력</h4>
                  {selectedStudent.recentEvents.map((event)=>(
                    <div className="history-line" key={event.eventId}>
                      <span><strong>{eventLabel(event.eventType)}</strong><small>
                        {event.fromGradeLevel ? `${event.fromGradeLevel}학년 ${event.fromClassLabel || ""}반` : ""}
                        {(event.fromGradeLevel || event.fromClassLabel) && (event.toGradeLevel || event.toClassLabel) ? " → " : ""}
                        {event.toGradeLevel ? `${event.toGradeLevel}학년 ${event.toClassLabel || ""}반` : ""}
                        {event.relatedSchoolName ? ` · ${event.relatedSchoolName}` : ""}
                      </small></span>
                      <b>{event.eventDate}</b>
                    </div>
                  ))}
                </section>
              </>
            ) : <p className="case-empty">학생을 선택하세요.</p>}
          </div>
        </div>
      )}
    </section>
  );
}

function Metric({label,value}:{label:string;value:number}){
  return <div className="erp-metric"><span>{label}</span><strong>{value}</strong></div>;
}
